import { ListControllerProps, RecordMap, useListController } from "ra-core";
import { createContext, FC, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react"
import { RecPatch } from "~/dataProvider/tools";
import { TableOwnershipDto } from "~/gen/api/governance";


// 
// 1. tables ownership list ctx
// 2. staged ownership edit/delete bag obj
// 3. lens methods for controlled input
// 4. staged edit counts
// 
// 

interface TablesOwnershipControllerInputProps {
  databaseId: number,

}

interface TablesOwnershipContextProps {
  databaseId: number,
  getCurrOwnerId: (tableId:number) => number | '',
  setCurrOwnerId: (tableId:number, ownerGroupId?:number) => void,
  editsCount: number,
  discardAllEdits: () => void,
  getStaged: () => Record<'edits'|'deletes', StagedEdits>,
  bulkAssignOwnerGroup: (ownerGroupId?: number) => void,
}


interface TablesOwnershipControllerProps {
  ownershipListCtx: ListControllerProps<TableOwnershipDto&RecPatch>,
  ownershipCtx: TablesOwnershipContextProps,
}

const TablesOwnershipContext = createContext<TablesOwnershipContextProps>({
  databaseId: -1,
  getCurrOwnerId: (tableId:number) => -1,
  setCurrOwnerId: (tableId:number, ownerGroupId?:number) => {},
  editsCount: 0,
  discardAllEdits: () => {},
  getStaged: (() => {}) as any,
  bulkAssignOwnerGroup: (ownerGroupId?: number) => {},
})


export const TablesOwnershipContextProvider: FC<{ value: TablesOwnershipContextProps }> = ({ children, value }) => {
  return (
    <TablesOwnershipContext.Provider value={value}>
      {children}
    </TablesOwnershipContext.Provider>
  )
}

export const useTablesOwnershipController = ({ databaseId }: TablesOwnershipControllerInputProps): TablesOwnershipControllerProps => {
  const [version, setVersion] = useState(0)
  
  const ownershipListCtx = useListController<TableOwnershipDto&RecPatch>({ resource: databaseId ? 'govrnc-data-ownership' : 'noop', basePath: '', filter:{databaseId}})

  const { data, loading, onUnselectItems } = ownershipListCtx
  const stagedRef = useRef<StageBage>({ edits: {}, deletes: {}, rawData: {}, databaseId })

  useEffect(() => {
    if (stagedRef.current.databaseId !== databaseId) {
      onUnselectItems()
    }
    if (loading) {
      stagedRef.current = { edits: {}, deletes: {}, rawData: {}, databaseId }
    } else {
      stagedRef.current.rawData = data
      setVersion(x => x + 1)
    }
  }, [loading, data, databaseId, onUnselectItems]) 

  const setCurrOwnerId = useCallback((tableId: number, ownerGroupId?: number) => {
    const staged = stagedRef.current
    const raw = staged.rawData[`${staged.databaseId}-${tableId}`]?.ownerGroupId

    const isEdit = !!ownerGroupId
    const toAppend = isEdit ? staged.edits : staged.deletes
    const toStrip = isEdit ? staged.deletes : staged.edits
    const appendVal = isEdit ? (ownerGroupId as number) : raw
    if (isEdit ? raw === ownerGroupId : !raw) { // revert back
      delete toAppend[tableId]
    } else {
      toAppend[tableId] = appendVal as number;
    }
    delete toStrip[tableId];
    
    setVersion(x => x + 1)
  }, [])

  const getCurrOwnerId = useCallback((tableId: number) => {
    const staged = stagedRef.current
    if (staged.deletes[tableId]) { return '' }
    if (staged.edits[tableId]) { return staged.edits[tableId] }
    
    return staged.rawData[`${staged.databaseId}-${tableId}`]?.ownerGroupId || ''
  }, [])

  const discardAllEdits = useCallback(() => {
    stagedRef.current.edits = {}
    stagedRef.current.deletes = {}
    setVersion(x => x + 1)
  }, [])

  const getStaged = useCallback(() => ({
    edits: {...stagedRef.current.edits},
    deletes: {...stagedRef.current.deletes},
  }), [])

  const listCtxRef = useRef(ownershipListCtx)
  listCtxRef.current = ownershipListCtx
  const bulkAssignOwnerGroup = useCallback((ownerGroupId?: number) => {
    const selectedIds = listCtxRef.current.selectedIds
    const onUnselectItems = listCtxRef.current.onUnselectItems
    if (!selectedIds || selectedIds.length === 0) return

    const tableIds = selectedIds.flatMap(id => {
      const tableId = (id as string).split('-')[1]
      return tableId ? [tableId] : []
    })
    for(const tableId of tableIds) {
      setCurrOwnerId(+tableId, ownerGroupId)
    }
    setVersion(x => x + 1)
    onUnselectItems()
  }, [setCurrOwnerId])

  const ownershipCtx = useMemo(() => ({
    databaseId: databaseId || -1,
    getCurrOwnerId,
    setCurrOwnerId,
    editsCount:
      Object.keys(stagedRef.current.deletes).length +
      Object.keys(stagedRef.current.edits).length,
    discardAllEdits,
    getStaged,
    bulkAssignOwnerGroup
  }), [databaseId, version, setCurrOwnerId, getCurrOwnerId, discardAllEdits, getStaged, bulkAssignOwnerGroup])

  return {
    ownershipListCtx,
    ownershipCtx
  }
}

export const useTablesOwnershipContext = () => useContext(TablesOwnershipContext);


interface StagedEdits { [x:number]: number }

interface StageBage {
  edits: StagedEdits,
  deletes: StagedEdits,
  rawData: RecordMap<TableOwnershipDto & RecPatch>,
  databaseId: number,
}
